/**
 * SuccessStep Component
 *
 * Step 4 (final) of the onboarding wizard - success confirmation screen.
 * Follows Shopify Polaris design guidelines for success states.
 *
 * Design Principles Applied:
 * - Uses Polaris Icon with tone="success" for semantic meaning
 * - Card with proper spacing using BlockStack and gap tokens
 * - ResourceList pattern for summary items
 * - Purposeful use of color (success tone, not decorative)
 * - Custom DiffuGen illustration for brand consistency
 */

import {
  BlockStack,
  InlineStack,
  Text,
  Icon,
  Box,
  Button,
  Card,
  Divider,
  Thumbnail,
} from '@shopify/polaris';
import {
  ProductIcon,
  AlertTriangleIcon,
  EmailIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@shopify/polaris-icons';
import type { WizardProduct } from './types';

/**
 * Props for the SuccessStep component
 */
export interface SuccessStepProps {
  /** Selected products with their details */
  products: WizardProduct[];
  /** Threshold configuration for alerts */
  thresholds: {
    urgentDays: number;
    warningDays: number;
  };
  /** Verified email address (null if not configured) */
  verifiedEmail: string | null;
  /** Callback when user clicks "Go to Dashboard" */
  onGoToDashboard: () => void;
  /** Callback when user clicks "Configure Another Product" */
  onConfigureAnother: () => void;
}

/**
 * Internal SummaryRow component following Polaris patterns
 */
function SummaryRow({
  icon,
  label,
  value,
}: {
  icon: typeof ProductIcon;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <InlineStack gap="300" blockAlign="center" wrap={false}>
      <Box>
        <Icon source={icon} tone="subdued" />
      </Box>
      <BlockStack gap="050">
        <Text as="span" variant="bodyMd" fontWeight="medium">
          {label}
        </Text>
        <Text as="span" variant="bodySm" tone="subdued">
          {value}
        </Text>
      </BlockStack>
    </InlineStack>
  );
}

/**
 * Product item display component
 */
function ProductItem({ product }: { product: WizardProduct }) {
  const placeholderImage = 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_medium.png';

  return (
    <InlineStack gap="300" blockAlign="center" wrap={false}>
      <Thumbnail
        source={product.imageUrl || placeholderImage}
        alt={product.title}
        size="small"
      />
      <BlockStack gap="050">
        <Text as="span" variant="bodyMd" fontWeight="medium">
          {product.title}
        </Text>
        {product.variantCount > 1 && (
          <Text as="span" variant="bodySm" tone="subdued">
            {product.variantCount} variants
          </Text>
        )}
      </BlockStack>
    </InlineStack>
  );
}

export function SuccessStep({
  products,
  thresholds,
  verifiedEmail,
  onGoToDashboard,
  onConfigureAnother,
}: SuccessStepProps) {
  return (
    <BlockStack gap="500">
      {/* Success Header with DiffuGen Illustration */}
      <BlockStack gap="400" inlineAlign="center">
        <div className="wizard-success-illustration">
          <img
            src="/images/empty-states/flux-dev_professional_clean_m_beae43ee.png"
            alt="Setup complete"
            style={{
              width: '160px',
              height: 'auto',
              mixBlendMode: 'multiply',
            }}
          />
        </div>
        <BlockStack gap="200" inlineAlign="center">
          <Text as="h2" variant="headingLg" fontWeight="semibold" alignment="center">
            You're all set!
          </Text>
          <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
            Stock Sentry is now monitoring your inventory. You'll receive alerts based on your preferences.
          </Text>
        </BlockStack>
      </BlockStack>

      {/* Setup Summary Card */}
      <Card>
        <BlockStack gap="400">
          <Text as="h3" variant="headingSm" fontWeight="semibold">
            Setup Summary
          </Text>

          <BlockStack gap="300">
            {/* Products Monitored */}
            <BlockStack gap="200">
              <InlineStack gap="300" blockAlign="center" wrap={false}>
                <Box>
                  <Icon source={ProductIcon} tone="subdued" />
                </Box>
                <Text as="span" variant="bodyMd" fontWeight="medium">
                  Products Monitored ({products.length})
                </Text>
              </InlineStack>

              {/* Product List */}
              <Box paddingInlineStart="600">
                <BlockStack gap="200">
                  {products.slice(0, 5).map((product) => (
                    <ProductItem key={product.id} product={product} />
                  ))}
                  {products.length > 5 && (
                    <Text as="span" variant="bodySm" tone="subdued">
                      +{products.length - 5} more product{products.length - 5 !== 1 ? 's' : ''}
                    </Text>
                  )}
                </BlockStack>
              </Box>
            </BlockStack>

            <Divider />

            {/* Alert Thresholds */}
            <BlockStack gap="200">
              <InlineStack gap="300" blockAlign="center" wrap={false}>
                <Box>
                  <Icon source={AlertTriangleIcon} tone="subdued" />
                </Box>
                <Text as="span" variant="bodyMd" fontWeight="medium">
                  Alert Thresholds
                </Text>
              </InlineStack>

              <Box paddingInlineStart="600">
                <BlockStack gap="100">
                  <InlineStack gap="200" blockAlign="center">
                    <Box
                      background="bg-fill-critical"
                      borderRadius="100"
                      paddingInline="150"
                      paddingBlock="050"
                    >
                      <Text as="span" variant="bodySm" fontWeight="medium">
                        Critical
                      </Text>
                    </Box>
                    <Text as="span" variant="bodySm" tone="subdued">
                      {thresholds.urgentDays} day{thresholds.urgentDays !== 1 ? 's' : ''} of stock remaining
                    </Text>
                  </InlineStack>
                  <InlineStack gap="200" blockAlign="center">
                    <Box
                      background="bg-fill-warning"
                      borderRadius="100"
                      paddingInline="150"
                      paddingBlock="050"
                    >
                      <Text as="span" variant="bodySm" fontWeight="medium">
                        Warning
                      </Text>
                    </Box>
                    <Text as="span" variant="bodySm" tone="subdued">
                      {thresholds.warningDays} day{thresholds.warningDays !== 1 ? 's' : ''} of stock remaining
                    </Text>
                  </InlineStack>
                </BlockStack>
              </Box>
            </BlockStack>

            <Divider />

            {/* Alert Recipient */}
            <SummaryRow
              icon={EmailIcon}
              label="Alert Recipient"
              value={
                verifiedEmail ? (
                  <InlineStack gap="100" blockAlign="center">
                    <Icon source={CheckCircleIcon} tone="success" />
                    <span>{verifiedEmail}</span>
                  </InlineStack>
                ) : (
                  <Text as="span" tone="subdued">
                    None configured - alerts will appear in dashboard only
                  </Text>
                )
              }
            />
          </BlockStack>
        </BlockStack>
      </Card>

      {/* Action Buttons */}
      <BlockStack gap="300">
        <Button variant="primary" size="large" fullWidth onClick={onGoToDashboard}>
          Go to Dashboard
        </Button>
        <Button variant="plain" fullWidth onClick={onConfigureAnother}>
          Configure Another Product
        </Button>
      </BlockStack>

      {/* Subtle status footer */}
      <Box paddingBlockStart="200">
        <InlineStack gap="200" align="center" blockAlign="center">
          <Icon source={CheckCircleIcon} tone="success" />
          <Text as="p" variant="bodySm" tone="subdued">
            Stock Sentry is now active
          </Text>
        </InlineStack>
      </Box>
    </BlockStack>
  );
}
